import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import NavigationItem from 'components/ui/NavigationItem';
import UserContextIndicator from 'components/ui/UserContextIndicator';

// Navigation items with role-based access control
const NAV_ITEMS = [
  {
    label: 'Panel de Control',
    path: '/administrative-dashboard',
    iconName: 'LayoutDashboard',
    roles: ['SUPER_ADMIN', 'ADMIN'],
    badgeKey: 'pendingApprovals',
  },
  {
    label: 'Analíticas de Engagement',
    path: '/analytics-dashboard',
    iconName: 'BarChart3',
    roles: ['SUPER_ADMIN', 'ADMIN'],
    badgeKey: null,
  },
  {
    label: 'Gestión de Usuarios',
    path: '/user-management',
    iconName: 'Users',
    roles: ['SUPER_ADMIN', 'ADMIN'],
    badgeKey: 'pendingUsers',
  },
  {
    label: 'Gestión de Actualizaciones',
    path: '/update-management',
    iconName: 'RefreshCw',
    roles: ['SUPER_ADMIN', 'ADMIN', 'OPERATOR'],
    badgeKey: 'pendingUpdates',
  },
  {
    label: 'Mi Dashboard',
    path: '/customer-user-dashboard',
    iconName: 'Monitor',
    roles: ['CUSTOMER_USER'],
    badgeKey: 'availableUpdates',
  },
  {
    label: 'Configuración del Sistema',
    path: '/system-configuration',
    iconName: 'Settings',
    roles: ['SUPER_ADMIN'],
    badgeKey: null,
  },
];

// Registration item shown separately
const REGISTRATION_ITEM = {
  label: 'Registro de Usuario',
  path: '/user-registration',
  iconName: 'UserPlus',
  roles: ['SUPER_ADMIN', 'ADMIN'],
  badgeKey: null,
};

const RoleBasedSidebar = ({
  user = { name: 'Carlos Rodríguez', role: 'ADMIN', organization: 'TechCorp S.A.S.' },
  notifications = { pendingApprovals: 3, pendingUsers: 0, pendingUpdates: 5, availableUpdates: 2 },
  isCollapsed: externalCollapsed,
  onCollapsedChange,
  onLogout,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const collapsed = externalCollapsed !== undefined ? externalCollapsed : isCollapsed;

  const handleCollapseToggle = () => {
    const next = !collapsed;
    setIsCollapsed(next);
    if (onCollapsedChange) onCollapsedChange(next);
  };

  const handleMobileToggle = () => setIsMobileOpen((prev) => !prev);
  const handleMobileClose = () => setIsMobileOpen(false);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu on ESC
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.key === 'Escape' && isMobileOpen) {
        setIsMobileOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMobileOpen]);

  const userRole = user?.role || 'CUSTOMER_USER';

  const visibleNavItems = NAV_ITEMS?.filter((item) =>
    item?.roles?.includes(userRole)
  );

  const showRegistration = REGISTRATION_ITEM?.roles?.includes(userRole);

  const getBadgeCount = (badgeKey) => {
    if (!badgeKey) return 0;
    return notifications?.[badgeKey] || 0;
  };

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        className="mobile-menu-btn lg:hidden"
        onClick={handleMobileToggle}
        aria-label={isMobileOpen ? 'Cerrar menú' : 'Abrir menú'}
        aria-expanded={isMobileOpen}
        aria-controls="sidebar-nav"
      >
        <Icon
          name={isMobileOpen ? 'X' : 'Menu'}
          size={20}
          color="#FFFFFF"
        />
      </button>
      {/* Mobile overlay */}
      <div
        className={`sidebar-overlay ${isMobileOpen ? 'visible' : ''}`}
        onClick={handleMobileClose}
        aria-hidden="true"
      />
      {/* Sidebar */}
      <aside
        id="sidebar-nav"
        role="navigation"
        aria-label="Navegación principal"
        className={`sidebar ${collapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}
      >
        {/* Header / Logo */}
        <div className="sidebar-header">
          <div className="sidebar-logo" aria-hidden="true">
            <img
              src="/assets/images/logo-1772764554588.png"
              alt="Vital Gestión en Salud logo"
              style={{ width: collapsed ? '32px' : '140px', height: 'auto', objectFit: 'contain', transition: 'width 0.2s ease', filter: 'brightness(0) invert(1)' }}
            />
          </div>

          <div className="sidebar-brand-text">
            <span
              style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '0.9375rem',
                fontWeight: 700,
                color: '#FFFFFF',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              }}
            >
              updatevitalftech
            </span>
            <span
              style={{
                fontFamily: 'var(--font-caption)',
                fontSize: '0.6875rem',
                color: 'rgba(255,255,255,0.7)',
                whiteSpace: 'nowrap',
              }}
            >
              Distribución de Software
            </span>
          </div>

          {/* Desktop collapse toggle */}
          <button
            className="sidebar-collapse-btn hidden lg:flex"
            onClick={handleCollapseToggle}
            aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
            title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          >
            <Icon
              name={collapsed ? 'ChevronRight' : 'ChevronLeft'}
              size={14}
              color="currentColor"
            />
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav" role="menu" aria-label="Menú de navegación">
          {/* Main navigation section */}
          <div className="nav-section-label">Principal</div>

          {visibleNavItems?.map((item) => (
            <NavigationItem
              key={item?.path}
              label={item?.label}
              path={item?.path}
              iconName={item?.iconName}
              badgeCount={getBadgeCount(item?.badgeKey)}
              isCollapsed={collapsed}
              onClick={handleMobileClose}
            />
          ))}

          {/* Registration section for admins */}
          {showRegistration && (
            <>
              <div
                className="nav-section-label"
                style={{ marginTop: '8px' }}
              >
                Administración
              </div>
              <NavigationItem
                label={REGISTRATION_ITEM?.label}
                path={REGISTRATION_ITEM?.path}
                iconName={REGISTRATION_ITEM?.iconName}
                badgeCount={0}
                isCollapsed={collapsed}
                onClick={handleMobileClose}
              />
            </>
          )}
        </nav>

        {/* Footer / User context */}
        <div className="sidebar-footer">
          <UserContextIndicator
            user={user}
            isCollapsed={collapsed}
            onLogout={onLogout}
          />
        </div>
      </aside>
    </>
  );
};

export default RoleBasedSidebar;